import { InlineIconButton, Pressable } from "@/components/ui/pressable";
import { SmilePlus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { ThreadNote } from "@/modules/threads/threadModel";
import {
  QUICK_REACTIONS,
  useToggleReaction,
  type ReactionSummary,
} from "@/modules/threads/useNoteReactions";

/** 👍 2  ✅ 1  [+😊] — click a chip to add or remove yours. */
export const ThreadReactions = ({
  note,
  reactions,
  memberNames,
}: {
  note: ThreadNote;
  reactions: ReactionSummary[];
  memberNames: Map<string, string>;
}) => {
  const toggle = useToggleReaction();
  return (
    <div className="flex flex-wrap items-center gap-1">
      {reactions.map((reaction) => (
        <Pressable
          key={reaction.emoji}
          onClick={() => void toggle(note, reaction.emoji, reaction.mine)}
          title={reaction.memberIds
            .map((id) => memberNames.get(String(id)) ?? "Teammate")
            .join(", ")}
          className={cn(
            "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs transition-colors",
            reaction.mine
              ? "border-primary/40 bg-primary/10 text-primary"
              : "bg-background hover:bg-accent",
          )}
        >
          <span>{reaction.emoji}</span>
          <span className="tabular-nums">{reaction.count}</span>
        </Pressable>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <InlineIconButton
            size="sm"
            aria-label="Add reaction"
            className="border border-transparent opacity-0 group-hover:opacity-100 hover:border-border focus-visible:opacity-100 data-[state=open]:opacity-100"
          >
            <SmilePlus className="size-3.5" />
          </InlineIconButton>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1" align="start">
          <div className="flex gap-0.5">
            {QUICK_REACTIONS.map((emoji) => {
              const mine = reactions.find((item) => item.emoji === emoji)?.mine;
              return (
                <Pressable
                  key={emoji}
                  onClick={() => void toggle(note, emoji, Boolean(mine))}
                  className={cn(
                    "flex size-8 items-center justify-center rounded-md text-lg hover:bg-accent",
                    mine && "bg-primary/10",
                  )}
                >
                  {emoji}
                </Pressable>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
